import { useGetInstanceGeneralSettings } from "@/api/user-settings";
import {
  FormLayoutElement,
  isLayoutElement,
} from "@/config/forms/general";
import { useAuth } from "@/context/auth/AuthContext";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useMemo } from "react";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";

const EXHIBITION_NUMBER_FIELD =
  "exhibition.expositionsDescriptionNumeroExpo";

/** Classic per-link notes — superseded by itinerantNote on each stop. */
function isClassicExhibitionNotesField(name: string): boolean {
  return (
    name === "oeuvresDansExpositionsNotes" ||
    name === "tirageDansExpositionsNotes" ||
    name === "sculptureEditionDansExpositionsNotes" ||
    name === "multiplesampleDansExpositionsNotes" ||
    /DansExpositionsNotes$/i.test(name)
  );
}

function mapItinerantFields(
  elements: FormLayoutElement[] | undefined,
  description: string | undefined,
  hideClassicNotes: boolean,
): FormLayoutElement[] | undefined {
  if (!elements) return elements;
  const mapped: FormLayoutElement[] = [];
  for (const element of elements) {
    if (isLayoutElement(element)) {
      const children = mapItinerantFields(
        element.children,
        description,
        hideClassicNotes,
      );
      if (children && children.length > 0) {
        mapped.push({ ...element, children });
      }
      continue;
    }
    if (hideClassicNotes && isClassicExhibitionNotesField(element.name)) {
      continue;
    }
    if (description && element.name === EXHIBITION_NUMBER_FIELD) {
      mapped.push({ ...element, description });
    } else {
      mapped.push(element);
    }
  }
  return mapped;
}

/**
 * Gates the itinerant management column on the instance setting, and builds the
 * blue hint under Exhibition number (on vs off copy).
 */
export function useItinerantDrawerChrome(
  itinerantConfig: ItinerantDrawerConfig | undefined,
  fields: FormLayoutElement[] | undefined,
) {
  const { data: generalSettings } = useGetInstanceGeneralSettings();
  const { instanceData } = useAuth();
  const vt = useVisibleT();

  const itinerantExhibitionGrouped =
    generalSettings?.itinerantExhibitionGrouped ??
    instanceData?.itinerantExhibitionGrouped ??
    false;

  const showItinerantSidebar = Boolean(itinerantConfig) && itinerantExhibitionGrouped;

  const exhibitionNumberHint = useMemo(() => {
    if (!itinerantConfig) return undefined;
    return itinerantExhibitionGrouped
      ? vt(
          "bo.itinerantExhibition.drawerHintOn",
          "Gérez les étapes de l’exposition itinérante dans la colonne de gauche ; l’ordre pilote l’affichage du résumé.",
        )
      : vt(
          "bo.itinerantExhibition.drawerHintOff",
          "Activez l’affichage groupé des expositions itinérantes dans les paramètres généraux pour gérer les étapes ici.",
        );
  }, [itinerantConfig, itinerantExhibitionGrouped, vt]);

  const fieldsWithHint = useMemo(
    () =>
      mapItinerantFields(
        fields,
        exhibitionNumberHint,
        Boolean(itinerantConfig),
      ),
    [fields, exhibitionNumberHint, itinerantConfig],
  );

  return {
    itinerantExhibitionGrouped,
    showItinerantSidebar,
    /** True when this drawer is in itinerant context (sidebar may still be gated). */
    isItinerantContext: Boolean(itinerantConfig),
    exhibitionNumberHint,
    fieldsWithHint: fieldsWithHint ?? fields ?? [],
  };
}
